import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertCircle,
    CheckCircle2,
    ExternalLink,
    Link2,
    LoaderCircle,
    MapPin,
    Plus,
    Save,
    ShieldCheck,
    Trash2
} from 'lucide-react';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import { adminAPI } from '../../utils/api.js';
import './ReviewLinksTab.scss';

const createRowId = () => {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    return `review-location-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const createEmptyLocation = () => ({
    id: createRowId(),
    branchId: '',
    branchName: '',
    address: '',
    reviewUrl: '',
    active: true
});

const normalizeLocation = (location = {}) => ({
    id: location.id || createRowId(),
    branchId: String(location.branchId || ''),
    branchName: String(location.branchName || ''),
    address: String(location.address || ''),
    reviewUrl: String(location.reviewUrl || ''),
    active: location.active !== false
});

const getSafePreviewUrl = (value) => {
    try {
        const parsed = new URL(value);
        return ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : null;
    } catch {
        return null;
    }
};

const ReviewLinksTab = () => {
    const { urlParams } = useAuth();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState(null);

    const activeCount = useMemo(
        () => locations.filter((location) => location.active).length,
        [locations]
    );

    const loadSettings = async () => {
        setLoading(true);
        setNotice(null);

        try {
            const response = await adminAPI.getReviewSolicitationSettings();
            const configuredLocations = Array.isArray(response.data?.reviewLocations)
                ? response.data.reviewLocations.map(normalizeLocation)
                : [];
            setLocations(configuredLocations);
        } catch (loadError) {
            setNotice({
                type: 'error',
                message: loadError.message || 'Unable to load review links.'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const updateLocation = (id, field, value) => {
        setLocations((current) => current.map((location) => (
            location.id === id
                ? { ...location, [field]: value }
                : location
        )));
        setNotice(null);
    };

    const addLocation = () => {
        setLocations((current) => [...current, createEmptyLocation()]);
        setNotice(null);
    };

    const removeLocation = (id) => {
        setLocations((current) => current.filter((location) => location.id !== id));
        setNotice(null);
    };

    const saveLocations = async () => {
        setSaving(true);
        setNotice(null);

        try {
            const response = await adminAPI.updateReviewSolicitationSettings({
                reviewLocations: locations
            });
            const savedLocations = Array.isArray(response.data?.reviewLocations)
                ? response.data.reviewLocations.map(normalizeLocation)
                : locations;
            setLocations(savedLocations);
            setNotice({
                type: 'success',
                message: `Saved ${savedLocations.length} review location${savedLocations.length === 1 ? '' : 's'} for this account location.`
            });
        } catch (saveError) {
            setNotice({
                type: 'error',
                message: saveError.message || 'Unable to save review links.'
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="review-links-tab review-links-tab--loading">
                <LoaderCircle className="review-links-tab__spinner" />
                <p>Loading review locations…</p>
            </div>
        );
    }

    return (
        <div className="review-links-tab">
            <section className="review-links-tab__hero">
                <div className="review-links-tab__hero-copy">
                    <div className="review-links-tab__hero-icon">
                        <MapPin />
                    </div>
                    <div>
                        <h1>Review Links</h1>
                        <p>
                            Map PestPac branches to the correct Google review page for
                            <strong> {urlParams.location || 'this location'}</strong>.
                        </p>
                    </div>
                </div>
                <div className="review-links-tab__summary">
                    <strong>{activeCount}</strong>
                    <span>active</span>
                </div>
            </section>

            <div className="review-links-tab__guidance">
                <ShieldCheck />
                <div>
                    <strong>Branch matching is deterministic</strong>
                    <p>
                        The completed appointment’s Branch ID is checked first, then its
                        branch name. The optional address is only an administrator label;
                        AI never guesses which review link to use.
                    </p>
                </div>
            </div>

            {notice && (
                <div className={`review-links-tab__notice review-links-tab__notice--${notice.type}`}>
                    {notice.type === 'success' ? <CheckCircle2 /> : <AlertCircle />}
                    <span>{notice.message}</span>
                </div>
            )}

            <section className="review-links-tab__panel">
                <div className="review-links-tab__panel-heading">
                    <div>
                        <h2>Branch mappings</h2>
                        <p>
                            Enter the Branch ID from PestPac whenever it is available.
                            Branch name provides a safe fallback for webhook payloads that omit the ID.
                        </p>
                    </div>
                    <button type="button" className="review-links-tab__add" onClick={addLocation}>
                        <Plus />
                        Add location
                    </button>
                </div>

                {locations.length === 0 ? (
                    <div className="review-links-tab__empty">
                        <Link2 />
                        <h3>No review locations configured</h3>
                        <p>Add the first PestPac branch and its Google review link.</p>
                        <button type="button" onClick={addLocation}>
                            <Plus />
                            Add location
                        </button>
                    </div>
                ) : (
                    <div className="review-links-tab__locations">
                        {locations.map((location, index) => (
                            <article className="review-links-tab__location" key={location.id}>
                                <div className="review-links-tab__location-heading">
                                    <div>
                                        <span>Review location {index + 1}</span>
                                        <strong>{location.branchName || location.branchId || 'New location'}</strong>
                                    </div>
                                    <div className="review-links-tab__location-actions">
                                        <label className="review-links-tab__toggle">
                                            <input
                                                type="checkbox"
                                                checked={location.active}
                                                onChange={(event) => updateLocation(
                                                    location.id,
                                                    'active',
                                                    event.target.checked
                                                )}
                                            />
                                            <span>Active</span>
                                        </label>
                                        <button
                                            type="button"
                                            className="review-links-tab__remove"
                                            onClick={() => removeLocation(location.id)}
                                            aria-label={`Remove review location ${index + 1}`}
                                        >
                                            <Trash2 />
                                        </button>
                                    </div>
                                </div>

                                <div className="review-links-tab__fields">
                                    <label>
                                        <span>PestPac Branch ID</span>
                                        <input
                                            value={location.branchId}
                                            onChange={(event) => updateLocation(
                                                location.id,
                                                'branchId',
                                                event.target.value
                                            )}
                                            placeholder="Example: 8"
                                            maxLength={120}
                                        />
                                        <small>Best identifier; copied from the completed webhook.</small>
                                    </label>

                                    <label>
                                        <span>Branch name</span>
                                        <input
                                            value={location.branchName}
                                            onChange={(event) => updateLocation(
                                                location.id,
                                                'branchName',
                                                event.target.value
                                            )}
                                            placeholder="Example: Coral Springs"
                                            maxLength={120}
                                        />
                                        <small>Used when PestPac sends a name instead of an ID.</small>
                                    </label>

                                    <label className="review-links-tab__field--wide">
                                        <span>Address or internal label <em>optional</em></span>
                                        <input
                                            value={location.address}
                                            onChange={(event) => updateLocation(
                                                location.id,
                                                'address',
                                                event.target.value
                                            )}
                                            placeholder="Example: Coral Springs, FL"
                                            maxLength={500}
                                        />
                                    </label>

                                    <label className="review-links-tab__field--wide">
                                        <span>Google review link</span>
                                        <div className="review-links-tab__url">
                                            <input
                                                type="url"
                                                value={location.reviewUrl}
                                                onChange={(event) => updateLocation(
                                                    location.id,
                                                    'reviewUrl',
                                                    event.target.value
                                                )}
                                                placeholder="https://g.page/r/.../review"
                                                maxLength={2048}
                                            />
                                            {getSafePreviewUrl(location.reviewUrl) && (
                                                <a
                                                    href={getSafePreviewUrl(location.reviewUrl)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    aria-label="Open review link in a new tab"
                                                >
                                                    <ExternalLink />
                                                </a>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                <div className="review-links-tab__footer">
                    <p>Changes affect only the location shown in the URL.</p>
                    <button
                        type="button"
                        className="review-links-tab__save"
                        onClick={saveLocations}
                        disabled={saving}
                    >
                        {saving ? <LoaderCircle className="review-links-tab__spinner" /> : <Save />}
                        {saving ? 'Saving…' : 'Save review links'}
                    </button>
                </div>
            </section>
        </div>
    );
};

export default ReviewLinksTab;
